import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Upload, CheckCircle2, PlayCircle, ExternalLink } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/apiClient";
import { Loader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function embedUrl(link) {
  if (!link) return null;
  try {
    if (link.includes("drive.google.com")) {
      const m = link.match(/\/d\/([^/]+)/);
      if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
      if (link.includes("/view")) return link.replace("/view", "/preview");
    }
    if (link.includes("youtube.com/watch")) { const id = new URL(link).searchParams.get("v"); return id ? `https://www.youtube.com/embed/${id}` : null; }
    if (link.includes("youtu.be/")) return `https://www.youtube.com/embed/${link.split("youtu.be/")[1].split(/[?&]/)[0]}`;
    if (link.includes("vimeo.com/")) { const id = link.split("vimeo.com/")[1].split(/[?&/]/)[0]; return `https://player.vimeo.com/video/${id}`; }
  } catch { return null; }
  return null;
}

export default function Video() {
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setV((await api.get("/video-section")).data);
  useEffect(() => { load(); }, []);
  if (!v) return <Loader />;

  const submitProof = async () => {
    if (!file) return toast.error("Choose your payment receipt");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/products/video/payment-proof`, fd); toast.success("Receipt submitted! Coach Reyland will email your access link once verified."); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const embed = embedUrl(v.video_link);
  return (
    <div className="max-w-3xl mx-auto">
      <Button variant="ghost" className="rounded-full mb-4 -ml-2" onClick={() => nav("/")} data-testid="video-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> Home</Button>
      <div className="bg-white border border-border rounded-2xl shadow-sm overflow-hidden">
        {v.image_url && !v.unlocked && (
          <img src={v.image_url.startsWith("http") ? v.image_url : fileUrl(v.image_url)} alt={v.title} className="w-full h-52 sm:h-64 object-cover" data-testid="video-cover" />
        )}
        <div className="p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange bg-orange/10 px-3 py-1.5 rounded-full"><PlayCircle className="w-3.5 h-3.5" /> Premium Video</span>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-3" data-testid="video-title">{v.title}</h1>
          <p className="text-muted-foreground mt-2 whitespace-pre-wrap">{v.description}</p>

          {v.unlocked ? (
            <div className="mt-6" data-testid="video-unlocked">
              <div className="p-4 rounded-xl bg-brandgreen/10 flex items-center gap-2 mb-4"><CheckCircle2 className="w-5 h-5 text-brandgreen" /><p className="text-sm font-medium">You have full access to this video.</p></div>
              {embed ? (
                <div className="rounded-xl overflow-hidden border border-border aspect-video" data-testid="video-embed">
                  <iframe src={embed} title={v.title} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen frameBorder="0" />
                </div>
              ) : v.video_link ? (
                <a href={v.video_link} target="_blank" rel="noreferrer" data-testid="video-link"><Button className="rounded-full bg-teal hover:bg-teal/90">Watch the Video <ExternalLink className="w-4 h-4 ml-1.5" /></Button></a>
              ) : (
                <p className="text-muted-foreground">The video link is being set up. Please check back shortly.</p>
              )}
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="p-5 rounded-xl bg-secondary/50">
                <p className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-teal" /> How to unlock</p>
                <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{v.instructions}</p>
                {v.price ? <p className="text-teal font-heading text-xl font-bold mt-3" data-testid="video-price">{v.price}</p> : null}
                {(v.payment_methods?.length > 0 || v.payment_instructions) && (
                  <div className="mt-4 space-y-3">
                    {v.payment_instructions && <p className="text-sm whitespace-pre-wrap">{v.payment_instructions}</p>}
                    {v.payment_methods?.map((m, i) => (
                      <div key={i} className="p-3 rounded-lg bg-white border border-border" data-testid={`video-pm-${i}`}>
                        <p className="font-medium text-sm">{m.label}</p>
                        {m.qr_url && <img src={m.qr_url.startsWith("http") ? m.qr_url : fileUrl(m.qr_url)} alt={m.label} className="w-40 h-40 object-contain my-2 border border-border rounded-lg" />}
                        {m.details && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{m.details}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="flex items-center gap-1.5"><Upload className="w-4 h-4" /> Submit payment receipt</Label>
                <div className="flex gap-2 mt-1">
                  <label className="flex-1 border border-border rounded-xl px-3 py-2 text-sm text-muted-foreground cursor-pointer truncate" data-testid="video-proof-dropzone">
                    {file ? file.name : "Choose receipt image..."}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="video-proof-file" />
                  </label>
                  <Button variant="outline" className="rounded-full" onClick={submitProof} disabled={busy} data-testid="video-submit-proof">Submit</Button>
                </div>
                {v.proof_status && v.proof_status !== "none" && <div className="mt-2 text-sm">Receipt status: <StatusBadge status={v.proof_status} /></div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
